import { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter, IActionType, VariableSelectFeatureFlag } from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Tabs } from '@makyoapp/makyo-ui'

import { parseFilterDataAsync } from '@/core/app-builder/parser/variable-text'
import { IRow } from '@/core/models/tables/row'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'

import { useWorkflowNodeContext } from '../../WorkflowActionFormModal'
import RowContextInput from './../RowContextInput'
import { PanelProps } from './panel.type'

export default function PanelFilterData({ data, inputValue, setValue }: PanelProps) {
  const { chosenTableId, conditions, returnFirstOnly } = data
  const { tables } = useDbClientContext()
  const nodeContext = useWorkflowNodeContext()
  const { dbClient, language, userSpecific } = useDbClientContext()
  const { activeLayerClient } = useActiveLayerContext()
  const [filteredData, setFilteredData] = useState<any[]>([])
  const [nonFilteredData, setNonFilteredData] = useState<any[]>([])
  const [activeTab, setActiveTab] = useState('filtered')
  const [rowIndex, setRowIndex] = useState('1')
  const previousNode = nodeContext?.previousNode

  const getInputData = useCallback(() => {
    switch (previousNode?.data?.actionValue?.type) {
      case IActionType.TemporaryVariable:
        return {
          value: inputValue,
          actionId: previousNode?.id,
        }
      case IActionType.FilterData:
        return {
          value: inputValue,
          actionId: previousNode?.id,
        }
    }

    return null
  }, [inputValue, previousNode?.data?.actionValue?.type, previousNode?.id])

  const { selectedTable } = useMemo(() => {
    const table = tables.find((tb) => tb.id === chosenTableId)
    if (!table) {
      return { selectedTable: null }
    }
    return { selectedTable: table }
  }, [tables, chosenTableId])

  const handleRowIndexChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    if (val === '-' || val === '') {
      setRowIndex(val)
    } else {
      const numVal = Number(val)
      if (!isNaN(numVal)) {
        setRowIndex(numVal.toString())
      }
    }
  }, [])

  const handleSimulate = useCallback(async () => {
    if (!nodeContext?.currentNode || !dbClient || !activeLayerClient) return

    const currentNode = nodeContext.currentNode
    const savedActionValue = currentNode.data?.actionValue

    // Use live form values if available, otherwise fall back to saved values
    const actionValue = savedActionValue

    if (!actionValue || actionValue.type !== IActionType.FilterData) return

    try {
      if (!chosenTableId) {
        throw new Error('No table selected for FilterData action')
      }

      const table = dbClient.tablesMap.get(chosenTableId)
      if (!table) {
        throw new Error(`Table ${chosenTableId} not found`)
      }

      const allDataResult = await table.GetFormattedData(language, userSpecific)
      const allRows = allDataResult.rows

      const theConditions = conditions?.conditions?.map((condition: any) => {
        const getRhs = (rhsCondition: any) => {
          try {
            if (!rhsCondition) {
              return rhsCondition
            }

            if (!rhsCondition?.actionType) {
              return rhsCondition
            }

            switch (rhsCondition.actionType) {
              case IActionType.TemporaryVariable:
                if (rhsCondition?.actionId === previousNode?.id) {
                  return inputValue
                }
                return rhsCondition.value?.value || rhsCondition.value || rhsCondition

              case IActionType.FilterData:
                if (rhsCondition?.actionId === previousNode?.id) {
                  const colId = rhsCondition.value?.colId
                  if (colId && inputValue && typeof inputValue === 'object') {
                    const columnValue = (inputValue as IRow)?.[colId]
                    return columnValue !== undefined && columnValue !== null ? columnValue.toString() : ''
                  }
                  return inputValue
                }
                const colId = rhsCondition.value?.colId
                const storedValue = rhsCondition.value?.value
                if (colId && storedValue && typeof storedValue === 'object') {
                  return storedValue[colId]?.toString() || ''
                }
                return storedValue || rhsCondition.value || rhsCondition

              case IActionType.AddNewRow:
              case IActionType.UpdateRow:
                if (rhsCondition?.actionId === previousNode?.id) {
                  return inputValue
                }
                return rhsCondition.value?.value || rhsCondition.value || rhsCondition

              default:
                if (rhsCondition?.actionId === previousNode?.id) {
                  return inputValue
                }
                return rhsCondition.value?.value || rhsCondition.value || rhsCondition
            }
          } catch (error) {
            console.warn('Error in getRhs function:', error, { rhsCondition, inputValue })
            return rhsCondition
          }
        }

        const rhs =
          condition?.rhs?.actionId === previousNode?.id
            ? {
                flag: VariableSelectFeatureFlag.ActionOutput,
                value: getRhs(condition.rhs),
              }
            : condition.rhs
        const rhsEnd =
          condition?.rhsEnd?.actionId === previousNode?.id
            ? {
                flag: VariableSelectFeatureFlag.ActionOutput,
                value: getRhs(condition.rhsEnd),
              }
            : condition.rhsEnd
        return {
          ...condition,
          rhs,
          ...(rhsEnd ? { rhsEnd } : {}),
        }
      })

      const tempActionForSimulation = {
        id: currentNode.id,
        type: IActionType.FilterData,
        actionType: IActionType.FilterData,
        settings: {
          tableId: chosenTableId,
          conditions: {
            conditions: theConditions,
            isAny: conditions?.isAny,
          },
          returnFirstOnly: returnFirstOnly,
        },
      }

      const allActions = [...(nodeContext?.allActions || []), tempActionForSimulation]
      const filteredRows = await parseFilterDataAsync(
        currentNode.id,
        allActions,
        dbClient,
        activeLayerClient,
        '',
        language,
        userSpecific,
        {
          detailsPage: {
            detailRowIndex: parseInt(rowIndex) - 1,
            detailTableId: chosenTableId,
          },
        }
      )

      const filteredRowIds = Array.isArray(filteredRows)
        ? filteredRows.map((row) => row._id || row.id)
        : filteredRows
          ? [filteredRows._id || filteredRows.id]
          : []

      const nonFilteredRows = allRows.filter((row) => !filteredRowIds.includes(row._id || row.id))

      setValue?.(filteredRows as IRow)

      setFilteredData(Array.isArray(filteredRows) ? filteredRows : filteredRows ? [filteredRows] : [])
      setNonFilteredData(nonFilteredRows)
    } catch (error) {
      console.error('Error simulating FilterData action:', error)
    }
  }, [
    nodeContext?.currentNode,
    nodeContext?.allActions,
    dbClient,
    activeLayerClient,
    chosenTableId,
    language,
    userSpecific,
    conditions,
    returnFirstOnly,
    rowIndex,
    setValue,
    previousNode?.id,
    inputValue,
  ])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSimulate()
    }, 500) // Debounce for 500ms

    return () => clearTimeout(timeoutId)
  }, [handleSimulate])

  const renderDataTable = (data: any[], title: string) => {
    const tableColumns = selectedTable?.columns || []
    const allColumns = [{ id: '_id', name: 'Row ID', type: ColumnTypes.RowID } as ColumnAdapter, ...tableColumns]

    return (
      <div className='space-y-3'>
        <div className='flex items-center justify-between'>
          <div className='text-sm font-semibold text-gray-700 dark:text-gray-300'>{title}</div>
          <div className='text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full'>
            {data?.length} {data?.length === 1 ? 'row' : 'rows'}
          </div>
        </div>
        {data?.length > 0 ? (
          <div className='bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden'>
            <div className='max-h-96 overflow-auto'>
              <table className='w-full'>
                <thead>
                  <tr className='bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600'>
                    {allColumns.map((column) => (
                      <th
                        key={column.id}
                        className='px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap sticky top-0 bg-gray-100 dark:bg-gray-700'
                      >
                        {column.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100 dark:divide-gray-700'>
                  {data?.slice(0, 10).map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {allColumns.map((column) => (
                        <td
                          key={column.id}
                          className='px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap max-w-48 truncate'
                        >
                          <div
                            className='truncate'
                            title={
                              row[column.id] !== undefined && row[column.id] !== null ? String(row[column.id]) : ''
                            }
                          >
                            {row[column.id] !== undefined && row[column.id] !== null ? (
                              String(row[column.id])
                            ) : (
                              <span className='text-gray-400 dark:text-gray-500 italic'>—</span>
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data?.length > 10 && (
              <div className='bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 px-4 py-3 text-center'>
                <span className='text-sm text-gray-600 dark:text-gray-400 font-medium'>
                  Showing 10 of {data?.length} rows
                </span>
                <span className='text-xs text-gray-500 dark:text-gray-500 ml-2'>
                  ({data?.length - 10} more rows available)
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className='bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center'>
            <div className='text-gray-400 dark:text-gray-500 mb-2'>
              <MaterialIcon icon='table_view' size={24} />
            </div>
            <div className='text-sm text-gray-500 dark:text-gray-400 font-medium'>No data available</div>
            <div className='text-xs text-gray-400 dark:text-gray-500 mt-1'>
              No rows match the current filter criteria
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <RowContextInput
        rowIndex={rowIndex}
        handleRowIndexChange={handleRowIndexChange}
        handleSimulate={handleSimulate}
        isLoading={false}
      />
      <div>
        <div className='text-sm font-medium text-gray-900 dark:text-white mb-2'>
          <Trans>Filter Results:</Trans>
        </div>
        <Tabs
          defaultActiveKey='filtered'
          onSelect={(key) => setActiveTab(key || 'filtered')}
          activeKey={activeTab}
          className='mt-4'
        >
          <Tabs.Nav className='border-b border-gray-200 dark:border-gray-600'>
            <Tabs.NavItem eventKey='filtered'>Filtered ({filteredData?.length})</Tabs.NavItem>
            <Tabs.NavItem eventKey='not-filtered'>Not Filtered ({nonFilteredData?.length})</Tabs.NavItem>
          </Tabs.Nav>
          <div className='mt-4'>
            <Tabs.Panel eventKey='filtered'>{renderDataTable(filteredData, 'Rows that passed the filter')}</Tabs.Panel>
            <Tabs.Panel eventKey='not-filtered'>
              {renderDataTable(nonFilteredData, 'Rows that did not pass the filter')}
            </Tabs.Panel>
          </div>
        </Tabs>
      </div>
    </>
  )
}
